import React, { useEffect, useState, useCallback } from "react";
import { Smartphone, Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// ─────────────────────────────────────────────────────────────────────────────
// PWA install — botão "Adicionar como App"
//
// Android Chrome dispara `beforeinstallprompt` automaticamente → clicar
// chama `prompt()` nativo (instalação real, sem dica). Após instalar,
// `appinstalled` dispara e o botão some.
//
// iOS Safari NÃO permite install programático (Apple bloqueia a API). Como
// não existe alternativa técnica, mostramos o passo a passo nativo.
//
// O componente exporta:
//  - <InstallPWAHeaderButton/> — versão compacta pra header mobile do painel.
// ─────────────────────────────────────────────────────────────────────────────

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();

function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return { deferredPrompt, installed, setDeferredPrompt, setInstalled };
}

export default function InstallPWAHeaderButton() {
  const { deferredPrompt, installed, setDeferredPrompt, setInstalled } = usePWAInstall();
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  const handleClick = useCallback(async () => {
    if (deferredPrompt) {
      // Android Chrome — instalação NATIVA real, sem dica.
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (isIOS()) {
      // iOS: única opção técnica é o tutorial.
      setIosGuideOpen(true);
      return;
    }
    // Android sem prompt disponível (já dispensou antes ou navegador não suporta).
    setIosGuideOpen(true);
  }, [deferredPrompt, setDeferredPrompt, setInstalled]);

  // Não mostra se: já instalado, NÃO é mobile, ou em iOS NÃO está no Safari.
  if (installed) return null;
  if (!isMobile()) return null;

  return (
    <>
      <button
        onClick={handleClick}
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white shadow-sm transition-colors"
        data-testid="pwa-install-btn"
        aria-label="Adicionar como App"
      >
        <Plus size={13} strokeWidth={2.5}/>
        Adicionar como App
      </button>

      <Dialog open={iosGuideOpen} onOpenChange={(o)=>!o && setIosGuideOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="pwa-ios-guide">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone size={18}/> {isIOS() ? "Instalar no iPhone" : "Instalar no celular"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {isIOS() ? (
              <>
                <p className="text-xs text-neutral-500">
                  O iPhone não permite instalação automática. Toque nos 3 passos abaixo:
                </p>
                <Step n="1">
                  Toque em <b><Share size={14} className="inline mb-0.5"/> Compartilhar</b> na barra inferior do Safari.
                </Step>
                <Step n="2">
                  Role para baixo e escolha <b>"Adicionar à Tela de Início"</b>.
                </Step>
                <Step n="3">
                  Confirme em <b>"Adicionar"</b> no canto superior direito. 🎉
                </Step>
                <p className="text-[11px] text-neutral-500 italic">
                  ⚠ Funciona apenas no Safari (não no Chrome do iPhone).
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-neutral-500">No Chrome do Android:</p>
                <Step n="1">Toque no menu <b>⋮</b> no topo direito.</Step>
                <Step n="2">Escolha <b>"Adicionar à tela inicial"</b> ou <b>"Instalar app"</b>.</Step>
                <Step n="3">Confirme em <b>"Adicionar"</b>. 🎉</Step>
              </>
            )}
            <Button
              onClick={()=>setIosGuideOpen(false)}
              className="w-full mt-2 bg-[#0A0A0A] hover:bg-black text-white"
              data-testid="pwa-guide-close"
            >Entendi</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center shrink-0 font-bold">{n}</div>
      <div className="flex-1 leading-snug pt-0.5">{children}</div>
    </div>
  );
}
